import React from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  AccessTimeRounded,
  AccountBalanceWalletOutlined,
  AddRounded,
  ListAltRounded,
  NorthEastRounded,
} from "@mui/icons-material";
import { convertToIndianFormat } from "../../../utils/functions";
import { formatWalletUpdatedAgo, getWalletMetrics } from "../utils/walletMetrics";
import { useSelector } from "react-redux";

const StatColumn = ({ label, value }) => (
  <Stack spacing={0.25} sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      sx={{
        color: "text.secondary",
        fontWeight: 700,
        letterSpacing: 0.6,
        fontSize: 11,
      }}
    >
      {label}
    </Typography>
    <Typography variant="subtitle1" fontWeight={800} sx={{ whiteSpace: "nowrap" }}>
      {convertToIndianFormat(value || 0)}
    </Typography>
  </Stack>
);

export default function WalletSummaryCard({
  summary,
  loading,
  onAddFunds,
  onWithdraw,
  onTransactions,
}) {
  const user = useSelector((state) => state.user?.data);
  if (loading) {
    return (
      <Skeleton
        variant="rounded"
        height={{ xs: 320, md: 220 }}
        sx={{ borderRadius: 3 }}
      />
    );
  }

  const metrics = getWalletMetrics(summary);
  const updatedLabel = formatWalletUpdatedAgo(metrics.lastUpdated);

  return (
    <Card
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        background: (theme) =>
          `linear-gradient(135deg, ${theme.palette.common.white} 0%, ${alpha(
            theme.palette.primary.main,
            0.04
          )} 55%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
        boxShadow: (theme) => `0 18px 40px ${alpha(theme.palette.common.black, 0.06)}`,
      }}
    >
      <Box sx={{ p: { xs: 2.5, md: 3 } }}>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={{ xs: 3, lg: 4 }}
          alignItems={{ lg: "stretch" }}
          justifyContent="space-between"
        >
          <Stack spacing={2.5} flex={1} minWidth={0}>
            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
              <Chip
                label="Active wallet"
                size="small"
                sx={{
                  fontWeight: 700,
                  color: "success.dark",
                  bgcolor: (theme) => alpha(theme.palette.success.main, 0.14),
                  borderRadius: 999,
                }}
              />
              {updatedLabel && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <AccessTimeRounded sx={{ fontSize: 16, color: "text.disabled" }} />
                  <Typography variant="caption" color="text.secondary">
                    {updatedLabel}
                  </Typography>
                </Stack>
              )}
            </Stack>

            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AccountBalanceWalletOutlined sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  Total wallet balance
                </Typography>
              </Stack>
              <Typography
                variant="h3"
                fontWeight={800}
                sx={{ fontSize: { xs: "2rem", md: "2.5rem" }, lineHeight: 1.1 }}
              >
                {convertToIndianFormat(metrics.total)}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={{ xs: 2, md: 4 }}
              flexWrap="wrap"
              useFlexGap
              sx={{
                pt: 0.5,
                borderTop: "1px solid",
                borderColor: (theme) => alpha(theme.palette.divider, 0.8),
              }}
            >
              <StatColumn label="AVAILABLE" value={metrics.available} />
              <StatColumn label="FROZEN" value={metrics.frozen} />
              <StatColumn label="WITHDRAWN" value={metrics.withdrawn} />
              <StatColumn label="TOTAL" value={metrics.total} />
            </Stack>
          </Stack>

          <Stack
            spacing={1.25}
            justifyContent="center"
            sx={{ width: { xs: "100%", lg: 220 }, flexShrink: 0 }}
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddRounded />}
              onClick={onAddFunds}
              disabled={user?.status !== "APPROVED"}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                py: 1.2,
                boxShadow: "none",
              }}
            >
              Add Funds
            </Button>
            <Button
              variant="contained"
              onClick={onWithdraw}
              startIcon={<NorthEastRounded />}
              disabled={user?.status !== "APPROVED"}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                py: 1.2,
                bgcolor: "#111111",
                color: "#fff",
                "&:hover": { bgcolor: "#252525" },
                boxShadow: "none",
              }}
            >
              Withdraw
            </Button>
            <Button
              variant="outlined"
              onClick={onTransactions}
              startIcon={<ListAltRounded />}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                py: 1.2,
                borderColor: "divider",
                color: "text.primary",
                "&:disabled": {
                  color: "text.disabled",
                  borderColor: "divider",
                },
              }}
            >
              Transactions
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Card>
  );
}
